import React, { useCallback, useEffect, useRef, useState } from 'react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import ManualExamMissedAction from './ManualExamMissedAction';

function apiRequest(path, options = {}) {
  const token = localStorage.getItem('school-token') || '';
  return fetch(path, {
    ...options,
    headers: {
      'x-access-token': token,
      ...(options.body ? { 'Content-Type': 'application/json' } : {}),
      ...(options.headers || {}),
    },
  }).then(async response => ({
    ok: response.ok,
    status: response.status,
    data: await response.json().catch(() => ({})),
  }));
}

function questionType(question) {
  return question.questionType || (question.options?.length ? 'mcq' : 'theory');
}

function scoreRow(rows, number, type) {
  return rows.find(row => Number(row.questionNumber) === Number(number)) || {
    questionNumber: number,
    questionType: type,
    chosenOption: null,
    scoreAwarded: null,
    remarks: '',
  };
}

function mergeRow(rows, number, patch, type) {
  const next = rows.filter(row => Number(row.questionNumber) !== Number(number));
  return [...next, { ...scoreRow(rows, number, type), ...patch }]
    .sort((left, right) => Number(left.questionNumber) - Number(right.questionNumber));
}

export default function ManualScoreEntryDrawer({ css, actionCss = css, actor, examId, examTitle, membershipId, student, onClose, onConfirmed, onMissed }) {
  const [questions, setQuestions] = useState([]);
  const [entry, setEntry] = useState(null);
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [feeBlocked, setFeeBlocked] = useState(false);
  const saveTimer = useRef(null);
  const basePath = `/${actor === 'teacher' ? 'teacher' : 'school'}/exams/${examId}/manual-score/${membershipId}`;

  const loadEntry = useCallback(async () => {
    setLoading(true);
    const result = await apiRequest(basePath);
    if (result.ok) {
      setFeeBlocked(false);
      setEntry(result.data.entry || null);
      setQuestions(result.data.questions || []);
      setDraft(result.data.draft || null);
    } else {
      setFeeBlocked(result.data.feeBlocked === true);
      setError(result.data.error || 'Could not load this score entry.');
    }
    setLoading(false);
  }, [basePath]);

  useEffect(() => { loadEntry(); }, [loadEntry]);

  const persist = useCallback(async rows => {
    setSaving(true);
    const result = await apiRequest(basePath, {
      method: 'PATCH',
      body: JSON.stringify({ perQuestionScores: rows }),
    });
    if (result.ok) {
      setFeeBlocked(false);
      setEntry(result.data.entry || null);
      setDraft(result.data.draft || null);
      setError('');
    } else {
      setFeeBlocked(result.data.feeBlocked === true);
      setError(result.data.error || 'Could not save the score entry.');
    }
    setSaving(false);
    return result.ok;
  }, [basePath]);

  const updateRow = (question, patch) => {
    if (!entry || entry.status === 'confirmed') return;
    const rows = mergeRow(entry.perQuestionScores || [], question.question_number, patch, questionType(question));
    setEntry(current => current ? { ...current, perQuestionScores: rows } : current);
    setError('');
    if (saveTimer.current) window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(() => persist(rows), 800);
  };

  const resetEntry = async () => {
    if (!window.confirm('Reset all recorded scores for this student?')) return;
    setBusy(true);
    if (saveTimer.current) window.clearTimeout(saveTimer.current);
    const result = await apiRequest(`${basePath}/reset`, { method: 'POST', body: JSON.stringify({}) });
    if (result.ok) {
      setEntry(result.data.entry || null);
      setDraft(result.data.draft || null);
      setError('');
    } else setError(result.data.error || 'Could not reset the score entry.');
    setBusy(false);
  };

  const confirmEntry = async () => {
    if (!entry || entry.status === 'confirmed') return;
    setBusy(true);
    if (saveTimer.current) window.clearTimeout(saveTimer.current);
    const saved = await persist(entry.perQuestionScores || []);
    if (!saved) {
      setBusy(false);
      return;
    }
    const result = await apiRequest(`${basePath}/confirm`, { method: 'POST', body: JSON.stringify({}) });
    if (result.ok) {
      onConfirmed?.();
      onClose();
    } else {
      setFeeBlocked(result.data.feeBlocked === true);
      setError(result.data.error || 'Could not confirm the score entry.');
    }
    setBusy(false);
  };

  useEffect(() => () => {
    if (saveTimer.current) window.clearTimeout(saveTimer.current);
  }, []);

  const rows = entry?.perQuestionScores || [];
  const scoredCount = rows.filter(row => row.scoreAwarded !== null && row.scoreAwarded !== undefined).length;
  const confirmed = entry?.status === 'confirmed';
  const studentName = student ? `${student.firstName || ''} ${student.lastName || ''}`.trim() : 'Student';

  return (
    <div className={css.drawerOverlay} onClick={onClose}>
      <div className={css.drawer} onClick={event => event.stopPropagation()}>
        <div className={css.drawerHeader}>
          <div>
            <div className={css.drawerTitleRow}>
              <div className={css.drawerTitle}>{studentName}</div>
              <span className={css.examModeBadge}>Manual Score Entry</span>
            </div>
            <div className={css.drawerSubtitle}>{student?.className || ''}{student?.classArm ? ` · ${student.classArm}` : ''} · {examTitle || 'Exam'} · Per-question recording</div>
          </div>
          <button className={css.closeBtn} type="button" onClick={onClose} aria-label="Close"><CloseIcon /></button>
        </div>
        <div className={css.drawerBody}>
          {loading ? <div className={css.drawerLoading}>Loading score entry...</div> : (
            <>
              {error && !feeBlocked && <div className={css.inlineError}>{error}</div>}
              {feeBlocked ? (
                <div className={css.drawerSection} style={{ border: '1px solid #ef9a9a', background: '#fff5f5' }} role="alert">
                  <div className={css.drawerSectionTitle} style={{ color: '#b71c1c' }}>Result Compilation Blocked</div>
                  <div className={css.inlineError} style={{ marginBottom: 10 }}>{error}</div>
                  <p style={{ color: '#7f1d1d', fontSize: '0.84rem', lineHeight: 1.55, margin: 0 }}>Outstanding school fees must be settled before this student&apos;s score can be added. The parent or guardian has been notified.</p>
                </div>
              ) : <>
              <div className={css.drawerSection} style={{ position: 'sticky', top: -20, zIndex: 2, background: '#fff', borderBottom: '1px solid #e8edf2' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                  <div>
                    <div className={css.drawerSectionTitle}>Score Entry Progress</div>
                    <div style={{ color: '#607d8b', fontSize: '0.82rem' }}>{scoredCount}/{questions.length} questions recorded</div>
                  </div>
                  <div style={{ color: '#1a6e73', fontWeight: 800, fontSize: '0.95rem' }}>
                    {draft?.score ?? 0}/{draft?.totalScore ?? 0} marks
                  </div>
                </div>
                {saving && <div style={{ color: '#78909c', fontSize: '0.74rem', marginTop: 5 }}><SaveIcon fontSize="inherit" /> Saving...</div>}
              </div>

              {questions.map(question => {
                const type = questionType(question);
                const row = scoreRow(rows, question.question_number, type);
                const maxScore = Number(question.score) || 0;
                return (
                  <div key={question.question_number} className={css.drawerSection} style={{ border: '1px solid #e5edef', borderRadius: 10, padding: 14, marginBottom: 10 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'flex-start' }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ color: '#1a6e73', fontSize: '0.73rem', fontWeight: 800, textTransform: 'uppercase' }}>Question {question.question_number} · {type === 'mcq' ? 'Multiple choice' : 'Theory'}</div>
                        <div style={{ color: '#1a2a36', fontWeight: 700, lineHeight: 1.45, marginTop: 4 }}>{question.question_text}</div>
                      </div>
                      <span className={css.chip} style={{ flexShrink: 0, background: '#e0f4f5', color: '#1a6e73', fontSize: '0.72rem' }}>{maxScore} marks</span>
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', margin: '9px 0' }}>
                      {question.topic && <span className={css.diffChip}>{question.topic}</span>}
                      {question.difficulty && <span className={css.diffChip}>{question.difficulty}</span>}
                    </div>
                    {question.marking_guide && <div style={{ color: '#607d8b', fontSize: '0.78rem', lineHeight: 1.5, marginBottom: 6 }}><strong>Marking guide:</strong> {question.marking_guide}</div>}
                    {question.model_answer && <div style={{ color: '#607d8b', fontSize: '0.78rem', lineHeight: 1.5, marginBottom: 8 }}><strong>Model answer:</strong> {question.model_answer}</div>}
                    {type === 'mcq' ? (
                      <div style={{ display: 'grid', gap: 6 }}>
                        <div style={{ color: '#546e7a', fontSize: '0.78rem', fontWeight: 700 }}>Student's selected answer</div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 6 }}>
                          <button type="button" disabled={confirmed || busy} onClick={() => updateRow(question, { chosenOption: '' })} style={{ padding: '9px 10px', border: `1px solid ${!row.chosenOption ? '#5fbec5' : '#dce7ea'}`, borderRadius: 7, background: !row.chosenOption ? '#e0f4f5' : '#fff', color: '#546e7a', textAlign: 'left', cursor: confirmed ? 'default' : 'pointer' }}>Not answered</button>
                          {(question.options || []).map((option, optionIndex) => {
                            const selected = row.chosenOption === option;
                            return <button key={`${question.question_number}-${optionIndex}`} type="button" disabled={confirmed || busy} onClick={() => updateRow(question, { chosenOption: option })} style={{ padding: '9px 10px', border: `1px solid ${selected ? '#5fbec5' : '#dce7ea'}`, borderRadius: 7, background: selected ? '#e0f4f5' : '#fff', color: '#1a2a36', textAlign: 'left', cursor: confirmed ? 'default' : 'pointer' }}>{option}</button>;
                          })}
                        </div>
                        {row.chosenOption && <div style={{ color: '#607d8b', fontSize: '0.76rem' }}>Correct answer: {question.correct_answer || 'Not provided'}</div>}
                      </div>
                    ) : (
                      <label className={css.formRow} style={{ margin: 0 }}>
                        <span className={css.formLabel}>Marks awarded (0 to {maxScore})</span>
                        <input className={css.formInput} type="number" min="0" max={maxScore} step="0.5" value={row.scoreAwarded ?? ''} disabled={confirmed || busy} onChange={event => updateRow(question, { scoreAwarded: event.target.value })} placeholder="Enter marks" />
                      </label>
                    )}
                    {!confirmed && <label className={css.formRow} style={{ margin: '10px 0 0' }}><span className={css.formLabel}>Remark (optional)</span><textarea className={css.formTextarea} rows={2} maxLength={300} value={row.remarks || ''} disabled={busy} onChange={event => updateRow(question, { remarks: event.target.value })} placeholder="Add a short marking note" /></label>}
                    {confirmed && row.remarks && <div style={{ color: '#607d8b', fontSize: '0.78rem', marginTop: 8 }}><strong>Remark:</strong> {row.remarks}</div>}
                  </div>
                );
              })}

              {draft && <>
                <div className={css.perfCardRow}>
                  <div className={css.perfCard}><div className={css.perfCardLabel}>Score</div><div className={css.perfCardValue}>{draft.score}</div></div>
                  <div className={css.perfCard}><div className={css.perfCardLabel}>Percentage</div><div className={css.perfCardValue}>{draft.percentage}%</div></div>
                  <div className={css.perfCard}><div className={css.perfCardLabel}>Result</div><div className={css.perfCardValue} style={{ color: draft.pass ? '#2e7d32' : '#c62828' }}>{draft.pass ? 'Pass' : 'Fail'}</div></div>
                </div>
                <div className={css.drawerSection}>
                  <div className={css.drawerSectionTitle}>Topic Breakdown</div>
                  <div className={css.topicPillRow}>{(draft.topicBreakdown || []).map(topic => {
                    const rate = Number(topic.avgCorrectRate) || 0;
                    const color = rate >= 70 ? '#10b981' : rate >= 40 ? '#f59e0b' : '#e53935';
                    return <div key={topic.topic} className={css.topicPill}><span className={css.topicPillName}>{topic.topic}</span><span className={css.topicPillStat} style={{ color }}>{topic.correct}/{topic.total} · {topic.avgCorrectRate}%</span></div>;
                  })}</div>
                </div>
                <div className={css.drawerSection}>
                  <div className={css.drawerSectionTitle}>Question Analysis</div>
                  <div className={css.tableWrap}><table className={css.table}><thead><tr><th>#</th><th>Question</th><th>Recorded</th><th>Result</th><th>Topic</th></tr></thead><tbody>{(draft.questionAnalysis || []).map(question => <tr key={question.number}><td>{question.number}</td><td className={css.qText}>{question.question}</td><td>{question.chose || '—'}</td><td>{question.correct ? 'Correct' : 'Review'}</td><td>{question.topic || 'General'}</td></tr>)}</tbody></table></div>
                </div>
              </>}
              </>}
            </>
          )}
        </div>
        {!loading && !confirmed && !feeBlocked && <div className={css.modalFooter} style={{ position: 'sticky', bottom: 0, background: '#fff', gap: 8, flexWrap: 'wrap' }}>
          <ManualExamMissedAction
            css={css}
            actionCss={actionCss}
            actor={actor}
            examMode="manual_scores"
            examId={examId}
            examTitle={examTitle}
            membershipId={membershipId}
            student={student}
            disabled={busy || saving}
            onMarked={() => { onMissed?.(); onClose(); }}
          />
          <button type="button" className={actionCss.btnSecondary} disabled={busy} onClick={resetEntry}><RestartAltIcon fontSize="small" /> Reset All</button>
          <button type="button" className={actionCss.btnPrimary} disabled={busy || saving || entry?.status !== 'entered'} onClick={confirmEntry}><CheckCircleIcon fontSize="small" /> Confirm &amp; Save Result</button>
        </div>}
      </div>
    </div>
  );
}